import { Navigate, Routes, Route } from 'react-router-dom';
import { Home } from './routes/Home';
import { Login } from './routes/auth/Login';
import { EmergencyLogin } from './routes/auth/EmergencyLogin';
import { ImportAttendees } from './routes/admin/ImportAttendees';
import { PreDayIssue } from './routes/admin/PreDayIssue';
import { TaskReviews } from './routes/admin/TaskReviews';
import { TaskReviewDetail } from './routes/admin/TaskReviewDetail';
import { ChecksAdmin } from './routes/admin/checks/ChecksAdmin';
import { KitTemplatesList } from './routes/admin/KitTemplatesList';
import { KitOrders } from './routes/admin/KitOrders';
import { KitTemplateForm } from './routes/admin/KitTemplateForm';
import { OnboardingTemplates } from './routes/admin/OnboardingTemplates';
import { OnboardingDocuments } from './routes/admin/OnboardingDocuments';
import { OnboardingForms } from './routes/admin/OnboardingForms';
import { OnboardingTaskCatalog } from './routes/admin/OnboardingTaskCatalog';
import { TrainingModules } from './routes/admin/TrainingModules';
import { TrainingProgress } from './routes/admin/TrainingProgress';
import { OnboardingDashboard } from './routes/onboarding/Dashboard';
import { OnboardingSubmissions } from './routes/onboarding/Submissions';
import { OnboardingSubmissionDetail } from './routes/onboarding/SubmissionDetail';
import { NewEmployeeWizard } from './routes/onboarding/NewEmployee';
import { CheckIn } from './routes/reception/CheckIn';
import { StationView } from './routes/stations/StationView';
import { CoordinatorGlobal } from './routes/coordinator/Global';
import { Register } from './routes/safety/Register';
import { Reports } from './routes/reports/Reports';
import { SelfService } from './routes/me/SelfService';
import { FormFill } from './routes/me/FormFill';
import { TaskComplete } from './routes/me/TaskComplete';
import { DocumentSign } from './routes/me/DocumentSign';
import { TrainingPlayer } from './routes/me/TrainingPlayer';
import { CertificatesAdmin } from './routes/admin/CertificatesAdmin';
import { FeedbackAdmin } from './routes/admin/FeedbackAdmin';
import { FeedbackForm } from './routes/public/FeedbackForm';
import { MobilisationsAdmin } from './routes/admin/MobilisationsAdmin';
import { LeaverReturn } from './routes/admin/LeaverReturn';
import { PlanonAdmin } from './routes/admin/PlanonAdmin';
import { SapAdmin } from './routes/admin/sap/SapAdmin';
import { SapMappings } from './routes/admin/sap/SapMappings';
import { PeopleSystemCreation } from './routes/admin/sap/PeopleSystemCreation';
import { UsersAdmin } from './routes/admin/users/UsersAdmin';
import { ActiveUsers } from './routes/admin/ActiveUsers';
import { FailedLogins } from './routes/admin/FailedLogins';
import { EmergencyAccessLog } from './routes/admin/EmergencyAccessLog';
import { PeopleAdmin } from './routes/admin/PeopleAdmin';
import { RolesAdmin } from './routes/admin/RolesAdmin';
import { PersonEdit } from './routes/admin/PersonEdit';
import { AttendeeNew } from './routes/admin/AttendeeNew';
import { SystemStatus } from './routes/admin/SystemStatus';
import { PrintableList } from './routes/admin/PrintableList';
import { AccessLog } from './routes/admin/AccessLog';
import { EmailLog } from './routes/admin/EmailLog';
import { EmailTemplates } from './routes/admin/EmailTemplates';
import { PdfTemplates } from './routes/admin/PdfTemplates';
import { StationSettings } from './routes/admin/StationSettings';
import { AssetsList } from './routes/admin/AssetsList';
import { AssetForm } from './routes/admin/AssetForm';
import { ImportAssets } from './routes/admin/ImportAssets';
import { Documents } from './routes/documents/Documents';
import { AppShell } from './components/layout/AppShell';
import { EmergencyBanner } from './components/layout/EmergencyBanner';
import { PrivacyNoticeGate } from './components/layout/PrivacyNoticeGate';
import { RequireStaff } from './components/auth/RequireStaff';

/**
 * The default ("main") portal: mobilisation, onboarding and welcome.
 * Public routes (login, magic-link forms) sit at the top level; everything
 * authenticated is nested inside <AppShell> so it gets the global TopNav.
 * The split is deliberate: showing nav links to "/admin" on a public PPE
 * form would be a bug.
 *
 * The training portal renders TrainingApp instead; the switch is in App.tsx.
 */
export function MainApp() {
  return (
    <>
      <EmergencyBanner />
      <PrivacyNoticeGate />
      <Routes>
        {/* Public routes: no top nav. */}
        <Route path="/login" element={<Login />} />
        <Route path="/emergency-login" element={<EmergencyLogin />} />
        <Route path="/f/:token" element={<FeedbackForm />} />

        {/* Authenticated app: wrapped in AppShell for the global nav.
            Staff-only pages are wrapped in <RequireStaff> so an inductee
            who types the URL is bounced to /me before the empty page
            flashes. The backend gates from the privacy commit are the
            actual enforcement. */}
        <Route element={<AppShell />}>
          <Route path="/" element={<Home />} />
          <Route path="/me" element={<SelfService />} />
          <Route path="/me/forms/:taskId" element={<FormFill />} />
          <Route path="/me/tasks/:taskId" element={<TaskComplete />} />
          <Route path="/me/sign/:taskId" element={<DocumentSign />} />
          <Route path="/me/training/:taskId" element={<TrainingPlayer />} />

          <Route
            path="/reception"
            element={
              <RequireStaff>
                <CheckIn />
              </RequireStaff>
            }
          />
          <Route
            path="/stations/:station"
            element={
              <RequireStaff>
                <StationView />
              </RequireStaff>
            }
          />
          <Route
            path="/coordinator"
            element={
              <RequireStaff>
                <CoordinatorGlobal />
              </RequireStaff>
            }
          />
          <Route
            path="/register"
            element={
              <RequireStaff>
                <Register />
              </RequireStaff>
            }
          />
          {/* Legacy `/roll-call` URL kept as a redirect for any
              bookmarks made before the UK rename. */}
          <Route path="/roll-call" element={<Navigate to="/register" replace />} />
          <Route
            path="/reports"
            element={
              <RequireStaff>
                <Reports />
              </RequireStaff>
            }
          />
          <Route
            path="/documents/*"
            element={
              <RequireStaff>
                <Documents />
              </RequireStaff>
            }
          />

          <Route
            path="/admin/import-attendees"
            element={
              <RequireStaff>
                <ImportAttendees />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/pre-day-issue"
            element={
              <RequireStaff>
                <PreDayIssue />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/task-reviews"
            element={
              <RequireStaff>
                <TaskReviews />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/task-reviews/:personId/:taskId"
            element={
              <RequireStaff>
                <TaskReviewDetail />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/checks/:checkType"
            element={
              <RequireStaff>
                <ChecksAdmin />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/kit-orders"
            element={
              <RequireStaff>
                <KitOrders />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/kit-templates"
            element={
              <RequireStaff>
                <KitTemplatesList />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/kit-templates/new"
            element={
              <RequireStaff>
                <KitTemplateForm />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/kit-templates/:id"
            element={
              <RequireStaff>
                <KitTemplateForm />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/roles"
            element={
              <RequireStaff>
                <RolesAdmin />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/onboarding-templates"
            element={
              <RequireStaff>
                <OnboardingTemplates />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/onboarding-documents"
            element={
              <RequireStaff>
                <OnboardingDocuments />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/onboarding-forms"
            element={
              <RequireStaff>
                <OnboardingForms />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/onboarding-tasks"
            element={
              <RequireStaff>
                <OnboardingTaskCatalog />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/training-modules"
            element={
              <RequireStaff>
                <TrainingModules />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/training-progress"
            element={
              <RequireStaff>
                <TrainingProgress />
              </RequireStaff>
            }
          />
          <Route
            path="/onboarding"
            element={
              <RequireStaff>
                <OnboardingDashboard />
              </RequireStaff>
            }
          />
          <Route
            path="/onboarding/new"
            element={
              <RequireStaff>
                <NewEmployeeWizard />
              </RequireStaff>
            }
          />
          <Route
            path="/onboarding/submissions"
            element={
              <RequireStaff>
                <OnboardingSubmissions />
              </RequireStaff>
            }
          />
          <Route
            path="/onboarding/submissions/:personId"
            element={
              <RequireStaff>
                <OnboardingSubmissionDetail />
              </RequireStaff>
            }
          />

          <Route
            path="/admin/certificates"
            element={
              <RequireStaff>
                <CertificatesAdmin />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/feedback"
            element={
              <RequireStaff>
                <FeedbackAdmin />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/mobilisations"
            element={
              <RequireStaff>
                <MobilisationsAdmin />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/leaver-return"
            element={
              <RequireStaff>
                <LeaverReturn />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/planon"
            element={
              <RequireStaff>
                <PlanonAdmin />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/sap"
            element={
              <RequireStaff>
                <SapAdmin />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/sap/mappings"
            element={
              <RequireStaff>
                <SapMappings />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/people-system-creation"
            element={
              <RequireStaff>
                <PeopleSystemCreation />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/activity"
            element={
              <RequireStaff>
                <ActiveUsers />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/failed-logins"
            element={
              <RequireStaff>
                <FailedLogins />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/users"
            element={
              <RequireStaff>
                <UsersAdmin />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/emergency-access"
            element={
              <RequireStaff>
                <EmergencyAccessLog />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/people"
            element={
              <RequireStaff>
                <PeopleAdmin />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/people/new"
            element={
              <RequireStaff>
                <AttendeeNew />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/people/:id"
            element={
              <RequireStaff>
                <PersonEdit />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/system"
            element={
              <RequireStaff>
                <SystemStatus />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/printable"
            element={
              <RequireStaff>
                <PrintableList />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/access-log"
            element={
              <RequireStaff>
                <AccessLog />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/email-log"
            element={
              <RequireStaff>
                <EmailLog />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/templates/email"
            element={
              <RequireStaff>
                <EmailTemplates />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/templates/pdf"
            element={
              <RequireStaff>
                <PdfTemplates />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/station-settings"
            element={
              <RequireStaff>
                <StationSettings />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/assets"
            element={
              <RequireStaff>
                <AssetsList />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/assets/new"
            element={
              <RequireStaff>
                <AssetForm mode="create" />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/assets/import"
            element={
              <RequireStaff>
                <ImportAssets />
              </RequireStaff>
            }
          />
          <Route
            path="/admin/assets/:id"
            element={
              <RequireStaff>
                <AssetForm mode="edit" />
              </RequireStaff>
            }
          />
        </Route>
      </Routes>
    </>
  );
}
